// Single output row: checkbox to select, weight slider (0.1-5), and an
// optional target sigma text input. When disabled (e.g. output is not
// available in the chosen oracle) the row is greyed and the checkbox
// cannot be toggled on.

import {
  Box,
  Checkbox,
  Flex,
  Slider,
  Text,
  TextField,
  Tooltip,
} from '@radix-ui/themes';
import { CanonicalOutputName, OutputSpec } from '../types';

type Props = {
  name: CanonicalOutputName;
  selected: boolean;
  spec?: OutputSpec;
  disabled?: boolean;
  disabledReason?: string;
  onToggle: (next: boolean) => void;
  onWeightChange: (weight: number) => void;
  onTargetSigmaChange: (value: number | undefined) => void;
};

export default function OutputRow({
  name,
  selected,
  spec,
  disabled,
  disabledReason,
  onToggle,
  onWeightChange,
  onTargetSigmaChange,
}: Props) {
  const row = (
    <Flex
      gap="3"
      align="center"
      style={{
        padding: '8px 10px',
        borderRadius: 6,
        background: selected && !disabled ? 'var(--gray-a2)' : 'transparent',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Checkbox
        checked={selected && !disabled}
        disabled={disabled}
        onCheckedChange={(v) => onToggle(Boolean(v))}
      />
      <Text weight="medium" style={{ minWidth: 48 }}>
        {name}
      </Text>

      <Flex align="center" gap="2" style={{ minWidth: 220 }}>
        <Text size="1" color="gray" style={{ width: 48 }}>
          weight
        </Text>
        <Box style={{ width: 120 }}>
          <Slider
            size="1"
            min={0.1}
            max={5}
            step={0.1}
            value={[spec?.weight ?? 1]}
            onValueChange={(v) => onWeightChange(v[0] ?? 1)}
            disabled={disabled || !selected}
          />
        </Box>
        <Text size="1" style={{ width: 28 }}>
          {(spec?.weight ?? 1).toFixed(1)}
        </Text>
      </Flex>

      <Flex align="center" gap="2">
        <Text size="1" color="gray">
          target sigma
        </Text>
        <TextField.Root
          size="1"
          type="number"
          placeholder="auto"
          value={spec?.targetSigma ?? ''}
          onChange={(e) => {
            const raw = e.currentTarget.value;
            const n = raw === '' ? undefined : Number(raw);
            onTargetSigmaChange(Number.isFinite(n) ? n : undefined);
          }}
          disabled={disabled || !selected}
          style={{ width: 90 }}
        />
      </Flex>
    </Flex>
  );

  if (disabled && disabledReason) {
    return <Tooltip content={disabledReason}>{row}</Tooltip>;
  }
  return row;
}
